import React from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import Icon from './Icon';
import './ReasoningCard.css';

/**
 * A widget's reasoning: what its query computes and what it assumed, as the SQL
 * writer described it when the widget was built (Figma "Reasoning Card"). One look
 * in all three places it appears: the widget creator's preview, the dashboard card
 * and the drill-down conversation.
 *
 *   <ReasoningCard rationale={{ description, assumptions }} />
 *   <ReasoningCard rationale={{ tiles: [{ label, description, assumptions }] }} onClose={hide} />
 *
 * A summary row runs one query per tile, so it has one reasoning per tile. Renders
 * nothing when there is nothing to say.
 */
export const WIDGET_REASONING_FLAG = 'widget_reasoning';

/**
 * Whether widget reasoning is shown. One reading of the flag for all three places.
 * `REACT_APP_FORCE_WIDGET_REASONING=true` turns it on for a local build whose Unleash
 * has no entry for it, as `REACT_APP_FORCE_INSIGHTS_DRILLDOWN` does for drill-down.
 */
export function useWidgetReasoningEnabled() {
  return useFlag(WIDGET_REASONING_FLAG) || process.env.REACT_APP_FORCE_WIDGET_REASONING === 'true';
}

const lines = (assumptions) => (Array.isArray(assumptions) ? assumptions.filter((a) => typeof a === 'string' && a.trim()) : []);

/** Whether a rationale (single or per-tile) has anything to show. */
export function hasReasoning(rationale) {
  if (!rationale || typeof rationale !== 'object') return false;
  if (Array.isArray(rationale.tiles)) return rationale.tiles.some(hasReasoning);
  return Boolean((rationale.description || '').trim()) || lines(rationale.assumptions).length > 0;
}

function Explanation({ rationale, label }) {
  const assumptions = lines(rationale.assumptions);
  return (
    <div className="reasoning-card-part">
      {(label || rationale.description) && (
        <p className="reasoning-card-description">
          {label && <span className="reasoning-card-label">{label}: </span>}
          {rationale.description}
        </p>
      )}
      {assumptions.length > 0 && (
        <ul className="reasoning-card-assumptions">
          {assumptions.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function ReasoningCard({ rationale, onClose, className = '', id }) {
  if (!hasReasoning(rationale)) return null;
  const parts = Array.isArray(rationale.tiles) ? rationale.tiles.filter(hasReasoning) : [rationale];
  return (
    <section id={id} className={`reasoning-card ${className}`.trim()} aria-label="Reasoning">
      <Icon name="lightbulb-filament" size={22} className="reasoning-card-glyph" />
      <div className="reasoning-card-body">
        <h4 className="reasoning-card-title">Reasoning</h4>
        {parts.map((part, i) => (
          <Explanation key={part.label || i} rationale={part} label={Array.isArray(rationale.tiles) ? part.label : undefined} />
        ))}
      </div>
      {onClose && (
        <button type="button" className="reasoning-card-close" aria-label="Close reasoning" title="Close" onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
      )}
    </section>
  );
}
